// ante-react-kit/src/components/AntPasscodeOverlay/index.js
// Overlay nhập mã PIN dùng chung (frosted glass + 6 ô PIN, hỗ trợ shake khi sai).
// Render qua Portal để không bị containing block của parent (vd: DevExtreme Toolbar
// Item) clip — luôn fullscreen ở body.
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './index.scss';

export default function AntPasscodeOverlay(props) {
    const {
        title,
        subtitle,
        length = 6,
        mask = true,
        numericOnly = true,
        onComplete,
        topSlot,
        bottomActions,
        processing = false,
        errorText = '',
        errorKey,
        dismissible = true,
        onDismiss,
        pinLabel = 'Nhập mã PIN',
        helperText
    } = props;

    const [value, setValue] = useState('');
    const [shake, setShake] = useState(false);
    // Khi user gõ lại sau 1 lỗi → ẩn error message (parent vẫn giữ errorText cho tới
    // lần fail kế tiếp). Reset cờ này mỗi khi parent báo lỗi mới (errorKey/errorText).
    const [errorDismissed, setErrorDismissed] = useState(false);
    const inputRef = useRef(null);
    const cardRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    // Chỉ shake khi có sự kiện lỗi MỚI: nếu parent truyền errorKey thì dùng nó (counter
    // tăng mỗi lần fail), tránh re-shake khi errorText chỉ đổi do countdown timer.
    const shakeDep = errorKey !== undefined ? errorKey : errorText;
    useEffect(() => {
        if (!errorText) return;
        setShake(true);
        setValue('');
        setErrorDismissed(false);
        const t = setTimeout(() => setShake(false), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shakeDep]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && dismissible && onDismiss) onDismiss();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [dismissible, onDismiss]);

    const handleChange = (e) => {
        let v = e.target.value || '';
        if (numericOnly) v = v.replace(/[^0-9]/g, '');
        v = v.slice(0, length);
        setValue(v);
        // User vừa gõ lại → ẩn error message của lần fail trước.
        if (v.length > 0 && !errorDismissed) setErrorDismissed(true);
        if (v.length === length && onComplete) onComplete(v);
    };

    const handleCardClick = () => {
        if (inputRef.current) inputRef.current.focus();
    };

    const boxes = Array.from({ length }, (_, i) => {
        const filled = i < value.length;
        const ch = filled ? (mask ? '•' : value[i]) : '';
        return (
            <div className="apt-passcode-box" data-filled={filled ? 1 : 0} key={i}>
                {ch}
            </div>
        );
    });

    const overlayNode = (
        <div
            className="apt-passcode-overlay"
            onMouseDown={(e) => {
                // click ngoài card → dismiss nếu cho phép
                if (dismissible && cardRef.current && !cardRef.current.contains(e.target) && onDismiss) onDismiss();
            }}
        >
            <div className={`apt-passcode-card ${shake ? 'is-error' : ''}`} ref={cardRef} onClick={handleCardClick}>
                {dismissible && onDismiss && (
                    <button type="button" className="apt-passcode-close" onClick={onDismiss} aria-label="close">
                        ×
                    </button>
                )}
                <div className="apt-passcode-title">{title}</div>
                {subtitle ? <div className="apt-passcode-subtitle">{subtitle}</div> : null}

                {topSlot ? <div className="apt-passcode-top">{topSlot}</div> : null}

                <div className="apt-passcode-label">{pinLabel}</div>
                <div className="apt-passcode-boxes">{boxes}</div>

                <input
                    ref={inputRef}
                    className="apt-passcode-hidden-input"
                    type={mask ? 'password' : 'text'}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={value}
                    onChange={handleChange}
                    onBlur={() => {
                        // Luôn giữ focus vào ô PIN — re-focus async để tránh
                        // race với dismiss/unmount.
                        setTimeout(() => {
                            const el = inputRef.current;
                            if (el && document.body.contains(el)) el.focus();
                        }, 0);
                    }}
                    autoComplete="off"
                    autoFocus
                />

                {errorText && !errorDismissed ? <div className="apt-passcode-error">{errorText}</div> : null}

                {helperText ? <div className="apt-passcode-helper">{helperText}</div> : null}

                {bottomActions ? <div className="apt-passcode-bottom">{bottomActions}</div> : null}

                {processing ? (
                    <div className="apt-passcode-processing">
                        <div className="apt-spinner" />
                    </div>
                ) : null}
            </div>
        </div>
    );

    if (typeof document === 'undefined' || !document.body) return overlayNode;
    return ReactDOM.createPortal(overlayNode, document.body);
}
